import { analyzeCategory, splitCategoryClauses } from '../../../utils/categoryClassifier';
import { isDebugFlagEnabled } from '../../../utils/debugTools';
import {
  hasStrongBehaviorActionSignal,
  hasStrongMedicationSignal,
  isSchoolIntroOrClosing,
} from './importLogsParsingClassification';
import { buildImportRowFromChunk } from './importLogsParsingChunks';
import { getLeadingImportDateHeading } from './importLogsDateAnchors';
import { buildChunkImportText } from './importLogsParsingTime';
import { buildImportCandidateRow } from './importLogsCandidateRowBuilders';
import { normalizeImportedLongNoteReviewRows } from './importLogsReviewNormalization';
import { splitIntoSentences } from './importLogsParsingText';
import { buildNaturalCaptureChunkPlan } from './naturalCaptureChunkingCore';
import { buildNaturalCaptureCategoryDecision } from './naturalCaptureCategoryCore';
import {
  LONG_NOTE_ACTIVITY_DOMINANT_PATTERNS,
  LONG_NOTE_ACTIVITY_PATTERNS,
  LONG_NOTE_BEHAVIOR_DOMINANT_PATTERNS,
  LONG_NOTE_FOOD_CONSUMPTION_PATTERNS,
  LONG_NOTE_FOOD_CONTEXT_PATTERNS,
  LONG_NOTE_MILESTONE_DOMINANT_PATTERNS,
  LONG_NOTE_MOOD_PATTERNS,
  LONG_NOTE_SLEEP_WITH_ROUGH_NIGHT_PATTERNS,
} from './importLogsLongNotePatterns';
import {
  CARE_MEDICATION_DOSE_MG_PATTERN,
  CARE_MEDICATION_LONG_NOTE_NAME_GLOBAL_PATTERN,
} from '../../../shared/careMedicationPatterns';
import { hasCareExplicitTime } from '../../../shared/careDateTimePatterns';

const LONG_NOTE_GREETING_PATTERNS = [
  /^(hi|hello|dear|good morning|good afternoon|good evening)\b/i,
  /^hi everyone\b/i,
  /^hi team\b/i,
];

const LONG_NOTE_SIGNATURE_PATTERNS = [
  /^(thanks|thank you|regards|sincerely|warmly|best)\b/i,
  /^(?:thanks|thank you),?$/i,
];

const LONG_NOTE_SENDER_LINE_PATTERN = /^(?:[A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,2}|[A-Z][A-Za-z.-]+)\.?$/;

const LONG_NOTE_SECTION_PATTERNS = [
  /\b(?:food|breakfast|lunch|dinner|snack|behavior|mood|medication|sleep|activity|school|teacher|recess|art class)\b/i,
];

const LONG_NOTE_SECTION_START_PATTERNS = [
  /^(?:at school pickup|during recess|behavior-wise|for mood|around bedtime|medication was given|this morning|dinner was|breakfast was|lunch was|snack was|she fell asleep|no side effects noticed|no aggressive behaviors observed|no side effects)\b/i,
];

const getNormalizedLine = (line = '') => String(line || '').trim();

const isImportParserDebugEnabled = () => {
  return isDebugFlagEnabled('debugImportParser');
};

const debugImportParser = (label, payload) => {
  if (!isImportParserDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(`[ImportParser] ${label}`, payload);
};

const hasStrongLongNoteActivitySignal = (text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable) {
    return false;
  }

  return LONG_NOTE_ACTIVITY_PATTERNS.some((pattern) => pattern.test(comparable));
};

const hasStrongLongNoteFoodSignal = (text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable) {
    return false;
  }

  const hasConsumption = LONG_NOTE_FOOD_CONSUMPTION_PATTERNS.some((pattern) => pattern.test(comparable));
  const hasFoodContext = LONG_NOTE_FOOD_CONTEXT_PATTERNS.some((pattern) => pattern.test(comparable));
  const hasMealHeader = /\b(?:breakfast|lunch|dinner|snack|meal)\b\s*(?:was|:)/i.test(comparable);

  if (hasMealHeader) {
    return true;
  }

  if (hasConsumption && hasFoodContext) {
    return true;
  }

  if (hasConsumption && /\b(?:before school|before leaving for school|after breakfast|after lunch|after dinner|at school pickup)\b/i.test(comparable)) {
    return true;
  }

  return false;
};

const startsNewLongNoteSection = (text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable) {
    return false;
  }

  return LONG_NOTE_SECTION_START_PATTERNS.some((pattern) => pattern.test(comparable));
};

const titleCase = (value = '') => String(value || '')
  .trim()
  .replace(/\s+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const buildLongNoteEventTitleHint = (category = 'log', text = '') => {
  const comparable = String(text || '').trim();
  const hasExplicitTime = hasCareExplicitTime(comparable);
  const isMorningTime = Boolean(hasExplicitTime && /(?:\b(?:6|7|8|9|10|11)\b|\b(?:am|morning|breakfast|before school)\b)/i.test(comparable));
  const isEveningTime = Boolean(hasExplicitTime && /(?:\b(?:4|5|6|7|8|9|10|11)\b|\b(?:pm|evening|dinner|bedtime|night)\b)/i.test(comparable));

  switch (String(category || '').toLowerCase()) {
    case 'medication': {
      if (/evening|bedtime|night/i.test(comparable)) return 'Evening meds';
      const medicationNames = [];
      const medicationNameMatches = comparable.match(CARE_MEDICATION_LONG_NOTE_NAME_GLOBAL_PATTERN) || [];
      medicationNameMatches.forEach((match) => medicationNames.push(titleCase(match)));
      if (medicationNames.length >= 2) return 'Evening meds';
      if (medicationNames[0]) {
        const doseMatch = comparable.match(CARE_MEDICATION_DOSE_MG_PATTERN);
        return doseMatch ? `${medicationNames[0]} ${doseMatch[0]}` : medicationNames[0];
      }
      return 'Medication';
    }
    case 'sleep':
      if (/\bfell asleep\b/i.test(comparable)) return 'Fell asleep';
      if (/\bwoke up\b/i.test(comparable) || /\bwoke\b/i.test(comparable)) return 'Wake';
      if (/\bbedtime\b/i.test(comparable)) return 'Bedtime';
      return 'Sleep';
    case 'food': {
      if (/\bbreakfast\b/i.test(comparable)) return 'Breakfast';
      if (/\blunch\b/i.test(comparable)) return 'Lunch';
      if (/\bdinner\b/i.test(comparable)) return 'Dinner';
      if (/\bsnack\b/i.test(comparable)) return 'Snack';
      if (/\bwater bottle\b/i.test(comparable)) return 'Before school';
      if (/\bjuice\b/i.test(comparable)) return 'Juice';
      if (/\bmost of\b/i.test(comparable)) return 'Dinner';
      if (/\beggs?\b|\bbagel\b|\bcream cheese\b|\bstrawberries?\b/i.test(comparable) && isMorningTime) return 'Breakfast';
      if (/\brice\b|\bchicken\b|\byogurt\b|\bcucumber\b/i.test(comparable) && isEveningTime) return 'Dinner';
      if (/\bbefore leaving for school\b|\bbefore school\b/i.test(comparable)) return 'Before school';
      return 'Food';
    }
    case 'activity': {
      if (/\bmusic therapy\b/i.test(comparable)) return 'Music therapy';
      if (/\bgroup reading\b/i.test(comparable)) return 'Group reading';
      if (/\bgroup painting\b/i.test(comparable)) return 'Group painting';
      if (/\bparticipat/i.test(comparable) && /\bprogress|improv(?:ed|ement)|better\b/i.test(comparable)) return 'Participation progress';
      if (/\bprogress|improv(?:ed|ement)|success(?:ful)?|proud|accomplishment|great job|did well|better than|nice improvement\b/i.test(comparable)) return 'Progress';
      if (/\bfocused\b/i.test(comparable)) return 'Focused work';
      const activityTitles = [
        ['Art class', /\bart class\b/i],
        ['Recess', /\brecess\b/i],
        ['School pickup', /\bschool pickup\b/i],
        ['Group painting', /\bgroup painting\b/i],
        ['Independent play', /\bplayed independently\b/i],
        ['Swing time', /\bswings?\b/i],
        ['Working with peers', /\bworking with the other kids\b/i],
      ];
      const match = activityTitles.find(([, pattern]) => pattern.test(comparable));
      if (match) return match[0];
      if (/\bparticipat/i.test(comparable)) return 'Participation';
      return 'Activity';
    }
    case 'behavior': {
      if (/\bno aggressive behavior(?:s)?\b/i.test(comparable) || /\bno aggression\b/i.test(comparable)) return 'No aggressive behaviors';
      if (/\btransition(?:ing)?\b/i.test(comparable) || /\bfrustrat/i.test(comparable)) {
        if (
          /\bbehavior-wise\b/i.test(comparable)
          || /\bthere was one moment\b/i.test(comparable)
          || /\bmath\b/i.test(comparable)
          || /\btask(?:s)?\b/i.test(comparable)
          || /\bclassroom\b/i.test(comparable)
          || /\bnoisy\b/i.test(comparable)
          || /\bheadphones\b/i.test(comparable)
          || /\bsensory break\b/i.test(comparable)
          || /\bcalm(?:ed)? down\b/i.test(comparable)
          || /\braised (?:her|his|their)? voice\b/i.test(comparable)
          || /\bcovered (?:her|his|their)? ears\b/i.test(comparable)
        ) {
          return 'Transition frustration';
        }
      }
      if (
        /\b(?:incident|escalat(?:ed|ion)|yell(?:ed|ing)?|push(?:ed|ing)?|hit(?:ting)?|kick(?:ed|ing)?|redirect(?:ed|ion)?|calm corner|deep breathing|sensory squeezes?|overstimulated)\b/i.test(comparable)
      ) {
        return 'Behavior incident';
      }
      if (/\bcovered (?:her|his|their)? ears\b|\bsensory break\b/i.test(comparable)) return 'Sensory break';
      if (/\braised (?:her|his|their)? voice\b/i.test(comparable)) return 'Behavior';
      return 'Behavior';
    }
    case 'mood': {
      const moodMatch = comparable.match(/\b(happy|affectionate|tired|anxious|worried|excited|calm|frustrated|upset|sad|content|nervous)\b/i);
      if (moodMatch?.[1]) return titleCase(moodMatch[1]);
      if (/\blaughed\b/i.test(comparable)) return 'Happy';
      return 'Mood';
    }
    default:
      return comparable.split(/(?<=[.!?])\s+/)[0]?.trim() || 'Note';
  }
};

const resolveLongNoteCategoryHint = (text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable) {
    return null;
  }

  if (
    /\bbedtime\b/i.test(comparable)
    && /\b(?:happy|affectionate|tired|anxious|worried|excited|calm|calmer|frustrated|upset|sad|content|nervous|proud|mixed|overstimulated|more relaxed)\b/i.test(comparable)
    && !/\b(?:fell asleep|asleep|woke up|woke)\b/i.test(comparable)
  ) {
    return 'mood';
  }

  if (hasStrongBehaviorActionSignal(comparable) || LONG_NOTE_BEHAVIOR_DOMINANT_PATTERNS.some((pattern) => pattern.test(comparable))) {
    return 'behavior';
  }

  if (hasStrongMedicationSignal(comparable)) {
    return 'medication';
  }

  if (hasStrongLongNoteFoodSignal(comparable)) {
    return 'food';
  }

  if (hasStrongLongNoteActivitySignal(comparable) || LONG_NOTE_ACTIVITY_DOMINANT_PATTERNS.some((pattern) => pattern.test(comparable))) {
    return 'activity';
  }

  if (LONG_NOTE_SLEEP_WITH_ROUGH_NIGHT_PATTERNS.some((pattern) => pattern.test(comparable))) {
    return 'sleep';
  }

  if (LONG_NOTE_MOOD_PATTERNS.some((pattern) => pattern.test(comparable))) {
    return 'mood';
  }

  if (LONG_NOTE_MILESTONE_DOMINANT_PATTERNS.some((pattern) => pattern.test(comparable))) {
    return 'milestone';
  }

  const analysis = analyzeCategory(comparable, { allowUnknown: true });
  return analysis?.category && analysis.category !== 'unknown' ? analysis.category : null;
};

const splitLongNoteBlockIntoEventSegments = (block = '', { enableDomainBoundarySplit = false } = {}) => {
  const sentences = enableDomainBoundarySplit
    ? splitCategoryClauses(block)
    : splitIntoSentences(block)
      .map((sentence) => String(sentence || '').trim())
      .map((sentence) => sentence.replace(/^\s*(?:[-*•]|\d+[.)])\s+/u, '').trim())
      .filter(Boolean);

  const segments = [];
  let current = null;

  const flush = () => {
    if (!current?.text) {
      current = null;
      return;
    }
    segments.push({
      text: current.text.trim(),
      category: current.category,
      sentenceCount: current.sentenceCount,
      sourceSentenceIndex: current.sourceSentenceIndex,
    });
    current = null;
  };

  sentences.forEach((sentence, sentenceIndex) => {
    const hasExplicitLeadingDateAnchor = Boolean(
      getLeadingImportDateHeading(sentence, {
        sourceText: block,
      })?.date
    );
    if (isSchoolIntroOrClosing(sentence) && !hasExplicitLeadingDateAnchor) {
      return;
    }

    const categoryHint = resolveLongNoteCategoryHint(sentence);
    const sectionStart = startsNewLongNoteSection(sentence);

    if (!current) {
      if (!categoryHint) {
        return;
      }
      current = {
        text: sentence,
        category: categoryHint,
        sentenceCount: 1,
        sourceSentenceIndex: sentenceIndex,
      };
      return;
    }

    const sameCategory = categoryHint && categoryHint === current.category;
    const behaviorContinuation = current.category === 'behavior' && categoryHint === 'mood' && /(?:calm|calmed|settled|regulated|headphones|sensory break|rejoined|returned to class|no aggressive behaviors|no aggression|no aggressive)/i.test(sentence);
    const foodContinuation = current.category === 'food' && categoryHint === 'food' && /(?:most of|few bites|finished|drank|ate|had more|wanted more|did not want|didn't want|water bottle|juice)/i.test(sentence);
    const moodContinuation = current.category === 'mood' && categoryHint === 'mood' && /(?:during dinner|during the evening|around bedtime|later in the evening|after dinner|bath time|routine changed)/i.test(sentence);
    const activityContinuation = current.category === 'activity' && categoryHint === 'activity' && /(?:art class|group painting|recess|swings|other kids|show her artwork|independently|teacher mentioned|school pickup|participated)/i.test(sentence);
    const medicationContinuation = current.category === 'medication' && categoryHint === 'medication' && /(?:no side effects|before bedtime|given at|mg|medication)/i.test(sentence);
    const sleepContinuation = current.category === 'sleep' && categoryHint === 'sleep' && /(?:around|woke|fell asleep|bedtime|asleep)/i.test(sentence);
    const currentLooksComplete = sectionStart && current.sentenceCount >= 1;

    if (
      !hasExplicitLeadingDateAnchor
      && (sameCategory || behaviorContinuation || foodContinuation || moodContinuation || activityContinuation || medicationContinuation || sleepContinuation)
    ) {
      current.text = `${current.text} ${sentence}`.replace(/\s+/g, ' ').trim();
      current.sentenceCount += 1;
      return;
    }

    if (hasExplicitLeadingDateAnchor || currentLooksComplete || (categoryHint && categoryHint !== current.category)) {
      flush();
      if (categoryHint) {
        current = {
          text: sentence,
          category: categoryHint,
          sentenceCount: 1,
          sourceSentenceIndex: sentenceIndex,
        };
      }
      return;
    }

    current.text = `${current.text} ${sentence}`.replace(/\s+/g, ' ').trim();
    current.sentenceCount += 1;
  });

  flush();
  return segments;
};

const isBoilerplateLine = (line = '', index = 0, lines = []) => {
  const normalized = getNormalizedLine(line);
  if (!normalized) {
    return true;
  }

  if (LONG_NOTE_GREETING_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return true;
  }

  if (LONG_NOTE_SIGNATURE_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return true;
  }

  const isStandaloneName = LONG_NOTE_SENDER_LINE_PATTERN.test(normalized);
  if (isStandaloneName) {
    const lastNonEmptyIndex = lines.reduce((acc, current, currentIndex) => (
      getNormalizedLine(current) ? currentIndex : acc
    ), -1);
    const isNearEnd = lastNonEmptyIndex >= 0 && index >= lastNonEmptyIndex - 1;
    return isNearEnd;
  }

  return false;
};

export const isLongNoteEmailText = (text = '') => {
  const rawText = String(text || '').trim();
  if (!rawText) {
    return false;
  }

  const lines = rawText.replace(/\r\n/g, '\n').split('\n');
  const paragraphs = rawText.split(/\n{2,}/).map((paragraph) => paragraph.trim()).filter(Boolean);
  const words = rawText.split(/\s+/).filter(Boolean);
  const hasGreetingOrSignature = lines.some((line) => (
    LONG_NOTE_GREETING_PATTERNS.some((pattern) => pattern.test(getNormalizedLine(line)))
    || LONG_NOTE_SIGNATURE_PATTERNS.some((pattern) => pattern.test(getNormalizedLine(line)))
    || LONG_NOTE_SENDER_LINE_PATTERN.test(getNormalizedLine(line))
  ));
  const hasMultiSectionLanguage = LONG_NOTE_SECTION_PATTERNS.some((pattern) => pattern.test(rawText))
    && paragraphs.length > 1;

  return Boolean(
    paragraphs.length > 1
    || hasGreetingOrSignature
    || words.length >= 90
    || rawText.length >= 450
    || hasMultiSectionLanguage
  );
};

export const stripLongNoteEmailBoilerplate = (text = '') => {
  const rawText = String(text || '').replace(/\r\n/g, '\n');
  if (!rawText.trim()) {
    return '';
  }

  const lines = rawText.split('\n');
  const cleanedLines = lines.filter((line, index) => {
    const normalized = getNormalizedLine(line);
    if (!normalized) {
      return true;
    }

    return !isBoilerplateLine(line, index, lines);
  });

  return cleanedLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

export const buildNaturalCaptureLongNoteRows = (text = '', {
  defaultDate = new Date(),
  childName = null,
  enableDomainBoundarySplit = false,
} = {}) => {
  const rawText = String(text || '').trim();
  if (!rawText) {
    return [];
  }

  const chunkPlan = buildNaturalCaptureChunkPlan(rawText, {
    inputShape: 'long_note_email',
  });
  const blocks = chunkPlan.blocks;
  if (!blocks.length) {
    return [];
  }

  const rows = blocks.flatMap((block, blockIndex) => {
    const segments = splitLongNoteBlockIntoEventSegments(block, { enableDomainBoundarySplit });
    const sourceBlockId = `long_note_email:${blockIndex}`;

    return segments.flatMap((segment, segmentIndex) => {
      const leadingDateHeading = getLeadingImportDateHeading(segment.text, {
        fallbackDate: defaultDate,
        sourceText: rawText,
      });
      const segmentTextForParsing = leadingDateHeading?.bodyText || segment.text;
      const { extracted, cleanText } = buildChunkImportText({ text: segmentTextForParsing });
      const categoryHint = segment.category || null;
      const category = categoryHint || 'log';
      const titleHint = buildLongNoteEventTitleHint(categoryHint || 'log', cleanText);
      debugImportParser('long-note-category-decision', {
        blockIndex,
        segmentIndex,
        categoryDecision: buildNaturalCaptureCategoryDecision(cleanText, {
          currentPathCategory: categoryHint,
          previousCategory: segmentIndex > 0 ? (segments[segmentIndex - 1]?.category || null) : null,
          sourceMode: 'long_note_email',
          inputMode: 'import',
          parserMode: 'long_note_email',
          shapeHint: 'long_note_email',
          categoryHint,
          confidenceMetadata: { confidence: 'high' },
          categorySource: segment.category ? 'segment_hint' : 'classified_hint',
        }),
        textPreview: cleanText.slice(0, 120),
      });
      const row = buildImportRowFromChunk({
        index: blockIndex * 100 + segmentIndex,
        defaultDate,
        childName,
        category,
        cleanText,
        finalTime: extracted.time,
        timeSource: extracted.time ? 'explicit' : 'missing',
        categorySource: 'long_note',
        confidenceState: { confidence: 'high', confidenceLabel: 'Looks right', confidenceReasons: [] },
        parsedObservation: true,
      });

      return [buildImportCandidateRow({
        ...row,
        title: '',
        titlePrefix: '',
        body: cleanText,
        text: cleanText,
        content: cleanText,
        details: cleanText,
        description: cleanText,
        summary: cleanText,
        value: cleanText,
        rawSourceText: block,
        sourceText: block,
        originalText: block,
        categoryHint,
        titleHint,
        parserMode: 'long_note_email',
        sourceMode: 'long_note_email',
        metadata: {
          ...(row.metadata || {}),
          longNoteMode: true,
          paragraphIndex: blockIndex,
          segmentIndex,
          originalRawText: rawText,
          sentenceCount: segment.sentenceCount,
        },
      }, {
        rawSourceText: block,
        sourceBlockId,
        sourceSentenceIndex: segment.sourceSentenceIndex,
        detectedDate: row?.date || null,
        detectedTime: extracted.time,
        categoryHint,
        titleHint,
        parserMode: 'long_note_email',
        sourceMode: 'long_note_email',
      })];
    });
  });

  return normalizeImportedLongNoteReviewRows(rows, {
    parserMode: 'long_note_email',
    sourceMode: 'long_note_email',
    inputMode: 'import',
    rawText,
  });
};
